import { graphql } from 'app/generated/gql'
import { withZod } from '@remix-validated-form/with-zod'
import { Plus as PlusIcon, WarningHexagon } from 'iconoir-react'
import { isEmpty, isEqual } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { z } from 'zod'
import Button from '../Button'
import { useConfirmation } from '../ConfirmationContext'
import FormCombo from '../form/FormCombo'
import FormInput from '../form/FormInput'
import { SmartForm, SubAction } from '../form/SmartForm'
import { closeModal } from 'app/lib/modal'
import { useMutation } from 'urql'
import { notify, notifyError, notifySuccess } from 'app/services/notify.client'
import { useWorkspace } from 'app/utils'
import { AdPlatformMappingType, defaultAdPlatformMappings, defaultAdPlatforms } from 'lib/adPlatformDefaults'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const CREATE_MUTATION = graphql(`
  mutation CreateAdPlatformMapping($input: CreateAdPlatformMappingInput!) {
    createAdPlatformMapping(input: $input) {
      id
      source
      medium
      platform
      orgId
      workspaceId
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateAdPlatformMapping($id: ID!, $input: UpdateAdPlatformMappingInput!) {
    updateAdPlatformMapping(id: $id, input: $input) {
      id
      source
      medium
      platform
      orgId
      workspaceId
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteAdPlatformMapping($id: ID!) {
    deleteAdPlatformMapping(id: $id)
  }
`)

const platformMappingValidator = withZod(
  z.object({
    source: z.string().min(1, { message: `Source is required` }),
    medium: z.string().min(1, { message: `Medium is required` }),
    platform: z.string().min(1, { message: `Platform is required` }),
  }),
)

export function AdPlatformMappingForm({
  platform,
  isDefaultPlatform = false,
  onSubmit,
}: {
  platform?: AdPlatformMappingType
  isDefaultPlatform: boolean
  onSubmit?: (data: { adPlatformMapping: Partial<AdPlatformMappingType> }) => void
}): ReactElement {
  const [confirm] = useConfirmation()
  const workspace = useWorkspace()
  const [createResult, executeCreate] = useMutation(CREATE_MUTATION)
  const [updateResult, executeUpdate] = useMutation(UPDATE_MUTATION)
  const [deleteResult, executeDelete] = useMutation(DELETE_MUTATION)
  const isLoading = createResult.fetching || updateResult.fetching || deleteResult.fetching
  const [subActionState, setSubActionState] = useState<SubAction | undefined>()
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false)
  const [selectedPlatform, setSelectedPlatform] = useState<OptionType | undefined>(
    platform?.platform ? { value: platform.platform, id: platform.id!, name: platform.platform } : undefined,
  )

  const adPlatformOptions = defaultAdPlatforms.map((platform) => ({
    id: platform,
    name: platform,
    value: platform,
  }))

  const handleSubmit = async (data: Partial<AdPlatformMappingType>, subAction: SubAction): Promise<void> => {
    setIsDuplicate(false)
    if (isDefaultPlatform) {
      notifyError(`Cannot create or edit default platform mapping`)

      return
    }
    const result = await platformMappingValidator.validate(data)
    if (workspace == null) {
      logError(`workspace not found`)
      notifyError(`Please select a workspace`)

      return
    }
    if (result.error) {
      logError(`error saving ad platform mapping`, result.error)
      notifyError(`Error Saving`)

      return
    }
    // checks if default mapping already exists
    if (
      defaultAdPlatformMappings.some(
        ([source, medium, platformName]) =>
          isEqual([source, medium, platformName], [result.data.source, result.data.medium, result.data.platform]) ||
          isEqual([source, medium, platformName], [result.data.medium, result.data.source, result.data.platform]),
      )
    ) {
      setIsDuplicate(true)
      return
    }
    if (subAction === SubAction.CREATE) {
      const { data, error } = await executeCreate({
        input: { source: result.data.source, medium: result.data.medium, platform: result.data.platform },
      })
      if (error) {
        setIsDuplicate(true)
        return
      }
      notifySuccess(`Platform Mapping Created`)
      onSubmit?.({ adPlatformMapping: data!.createAdPlatformMapping })
      closeModal()
    } else if (subAction === SubAction.EDIT) {
      if (
        result.data.platform === platform?.platform &&
        result.data.source === platform?.source &&
        result.data.medium === platform?.medium
      ) {
        notify(`Please make a change to save the platform mapping.`)
        return
      }
      const { data, error } = await executeUpdate({
        id: platform!.id!,
        input: { source: result.data.source, medium: result.data.medium, platform: result.data.platform },
      })
      if (error) {
        setIsDuplicate(true)
        return
      }
      notifySuccess(`Change Saved. This may take some time to fully apply.`)
      onSubmit?.({ adPlatformMapping: data!.updateAdPlatformMapping })
      closeModal()
    }
  }

  const handleDelete = async (id: string) => {
    if (isDefaultPlatform) return
    const { error } = await executeDelete({ id }, { additionalTypenames: ['AdPlatformMapping'] })
    if (error) {
      notifyError(`Error Deleting`)
      return
    }
    notifySuccess(`Deleted Successfully`)
    closeModal()
  }

  useEffect(() => {
    if (isEmpty(platform) || platform?.id == null) {
      setSubActionState(SubAction.CREATE)
    } else setSubActionState(SubAction.EDIT)
  }, [platform])

  return (
    <SmartForm<Partial<AdPlatformMappingType>>
      id="ad-platform-mapping-form"
      validator={platformMappingValidator}
      className="mb-0 mt-0 flex w-full flex-col gap-y-0"
      defaultValues={platform}
      onSubmit={(data) => handleSubmit(data, isEmpty(platform) ? SubAction.CREATE : SubAction.EDIT)}
      hiddenInputs={[{ name: `platformI d`, value: platform?.id }]}
      hideButtons
      preventSubmit
    >
      <div className="col-span-6 flex flex-col px-6 py-4">
        <div className="flex items-start gap-x-2">
          <FormInput
            labelClassName="text-strong text-body-sm font-medium"
            className="h-10 w-44 !px-2 !py-1"
            name="source"
            type="text"
            label="Source"
            cols={3}
          />
          <PlusIcon className="size-5 mt-9" strokeWidth={1.5} />
          <FormInput
            labelClassName="text-strong w-44 text-body-sm font-medium"
            className="h-10 !px-2 !py-1"
            name="medium"
            type="text"
            label="Medium"
            cols={3}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-5 mt-9"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.499 8.248h15m-15 7.501h15" />
          </svg>
          <FormCombo
            label="Platform"
            labelClassName="text-strong w-44 text-body-sm font-medium"
            inputClassName="!h-10 !px-2 !py-1 text-body-sm font-medium"
            name="platform"
            placeholder="Select Platform"
            value={selectedPlatform}
            options={adPlatformOptions}
            onChange={setSelectedPlatform}
            cols={3}
          />
        </div>
      </div>
      <div className="border-t-trn col-span-6 flex w-full items-center justify-between border px-6 py-3">
        <Button
          mode="danger"
          size="sm"
          label="Delete"
          className={`mr-auto ${subActionState === SubAction.EDIT ? `` : `invisible`}`}
          onClick={async () => {
            await confirm({
              title: `Delete platform mapping`,
              description: `Are you sure you want to delete the platform mapping`,
              variant: `danger`,
            }).then(async () => handleDelete(platform?.id!))
          }}
          loading={isLoading}
        />
        {
          <div
            className={`${
              isDuplicate ? `inline-flex` : `invisible`
            } w-full items-center justify-center gap-x-2 text-center text-red-700`}
          >
            <WarningHexagon className="size-4" />
            <span className="text-body-xs">This Mapping Already Exists</span>
          </div>
        }
        {subActionState === SubAction.CREATE && (
          <Button mode="primary" size="sm" label="Create" type="submit" className="text-center" loading={isLoading} />
        )}
        {subActionState === SubAction.EDIT && (
          <Button mode="primary" size="sm" label="Update" type="submit" className="text-center" loading={isLoading} />
        )}
      </div>
    </SmartForm>
  )
}
